import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './MerchantPage.css';

interface MerchantStats {
  balanceUsdt: string;
  totalEarnedUsdt: string;
  merchantEarnedUsdt: string;
  merchantOrders: number;
  activeTokens: number;
}

interface TokenItem {
  id: string;
  name: string;
  type: 'publishable' | 'secret';
  prefix: string;
  token?: string; // decrypted value (only for publishable, returned by API)
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  totalOrders: number;
}

interface MerchantOrder {
  orderId: string;
  merchantOrderId: string | null;
  status: string;
  usdtAmount: number;
  currency: string;
  sandbox: boolean;
  webhookStatus: string;
  webhookAttempts: number;
  webhookLastError: string | null;
  webhookDeliveredAt: string | null;
  callbackUrl: string | null;
  confirmedAt: string | null;
  createdAt: string;
  token: { id: string; name: string; prefix: string } | null;
}

interface CreatedPair {
  publishable: { id: string; name: string; type: string; token: string; prefix: string };
  secret: { id: string; name: string; type: string; token: string; prefix: string };
}

type Tab = 'keys' | 'orders' | 'quickstart' | 'webhooks';

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

export default function MerchantPage() {
  const [stats, setStats] = useState<MerchantStats | null>(null);
  const [tokens, setTokens] = useState<TokenItem[]>([]);
  const [orders, setOrders] = useState<MerchantOrder[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [tab, setTab] = useState<Tab>('keys');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdPair, setCreatedPair] = useState<CreatedPair | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [viewToken, setViewToken] = useState<{ id: string; type: string; name: string; value: string | null } | null>(null);
  const [revealLoading, setRevealLoading] = useState<string | null>(null);
  const [orderFilter, setOrderFilter] = useState<{ status: string; dateFrom: string; dateTo: string; sandbox: string }>({ status: '', dateFrom: '', dateTo: '', sandbox: '' });
  const [webhookInfoOrder, setWebhookInfoOrder] = useState<MerchantOrder | null>(null);
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState<'ok' | 'err' | null>(null);
  const [codeTab, setCodeTab] = useState<'script' | 'prog'>('script');
  const sdkUrl = `${window.location.origin}/sdk/plzpay.js`;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/merchant/stats', { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const fetchTokens = useCallback(async () => {
    try {
      const res = await fetch('/api/merchant/tokens', { credentials: 'include' });
      if (res.ok) setTokens(await res.json());
    } catch {}
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (orderFilter.status) params.set('status', orderFilter.status);
      if (orderFilter.dateFrom) params.set('dateFrom', orderFilter.dateFrom);
      if (orderFilter.dateTo) params.set('dateTo', orderFilter.dateTo);
      if (orderFilter.sandbox !== '') params.set('sandbox', orderFilter.sandbox);
      const res = await fetch(`/api/merchant/orders?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.items);
        setOrdersTotal(data.total);
      }
    } catch {}
  }, [orderFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStats(), fetchTokens(), fetchOrders()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleCopy = useCallback((text: string, key: string) => {
    copyText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  const handleCreatePair = useCallback(async () => {
    setCreating(true);
    try {
      const res = await fetch('/api/merchant/tokens', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTokenName || 'My Shop' }),
      });
      if (res.ok) {
        const pair: CreatedPair = await res.json();
        setCreatedPair(pair);
        fetchTokens();
        fetchStats();
      }
    } catch {}
    setCreating(false);
  }, [newTokenName, fetchTokens, fetchStats]);

  const handleRevoke = useCallback(async (tokenId: string) => {
    await fetch(`/api/merchant/tokens/${tokenId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    fetchTokens();
    fetchStats();
  }, [fetchTokens, fetchStats]);

  const handleViewToken = useCallback(async (t: TokenItem) => {
    if (t.type === 'publishable' && t.token) {
      setViewToken({ id: t.id, type: t.type, name: t.name, value: t.token });
      return;
    }
    setRevealLoading(t.id);
    try {
      const res = await fetch(`/api/merchant/tokens/${t.id}/reveal`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setViewToken({ id: t.id, type: t.type, name: t.name, value: data.token });
      }
    } catch {}
    setRevealLoading(null);
  }, []);

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreatedPair(null);
    setNewTokenName('');
  }, []);

  const handleResendWebhook = useCallback(async (orderId: string) => {
    setResending(true);
    setResendResult(null);
    try {
      const res = await fetch(`/api/merchant/orders/${orderId}/webhook/resend`, {
        method: 'POST',
        credentials: 'include',
      });
      setResendResult(res.ok ? 'ok' : 'err');
      if (res.ok) {
        // Refresh orders list so webhook status updates
        setTimeout(() => fetchOrders(), 1500);
      }
    } catch {
      setResendResult('err');
    }
    setResending(false);
  }, [fetchOrders]);

  const pubToken = tokens.find(t => t.type === 'publishable' && t.isActive);
  const pubKeyValue = pubToken?.token ?? pubToken?.prefix ?? 'plzpay_pub_YOUR_TOKEN';

  const scriptSnippet = `<!-- Step 1: add a button with data attributes -->
<button data-plzpay
  data-token="${pubKeyValue}"
  data-amount="10"
  data-currency="USDT"
  data-order-id="order-123"
  data-comeback-url="https://yoursite.com/success"
  data-iframe="true"
  data-sandbox="false">
  Pay 10 USDT
</button>

<!-- Step 2: load SDK (once, before </body>) -->
<script src="${sdkUrl}"></script>`;

  const progSnippet = `<!DOCTYPE html>
<html>
<body>

  <!-- Your button -->
  <button id="pay-btn">Pay 10 USDT</button>

  <!-- Load SDK before </body> so DOM is ready -->
  <script src="${sdkUrl}"></script>

  <script>
    // Create PlzPay instance inside click handler
    // (PlzPay is guaranteed to be loaded by the time user clicks)
    document.getElementById('pay-btn').addEventListener('click', function() {
      new PlzPay({
        token: '${pubKeyValue}',
        amount: 10,
        currency: 'USDT',
        orderId: 'order-' + Date.now(),   // unique per click
        description: 'Order #123',
        callbackUrl: 'https://yoursite.com/webhook',
        comebackUrl: 'https://yoursite.com/success',
        iframe: true,
        ttl: 30,
        sandbox: false,        // true = test order (not real payment)
        onSuccess: function(data) { console.log('Paid!', data); },
        onError: function(err) { console.error(err); },
        onClose: function() { console.log('Modal closed'); },
      }).open();
    });
  </script>

</body>
</html>`;

  const webhookVerifySnippet = `const crypto = require('crypto');

function verifyWebhook(body, signature, secretKey) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secretKey)
    .update(body)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

// In your Express route:
app.post('/webhook', (req, res) => {
  const sig = req.headers['x-plzpay-signature'];
  const raw = req.rawBody; // use bodyParser with verify
  if (!verifyWebhook(raw, sig, process.env.PLZPAY_SECRET)) {
    return res.status(401).send('Invalid signature');
  }
  const event = req.body;
  if (event.event === 'payment.completed') {
    console.log('Payment confirmed:', event.orderId);
  }
  res.json({ ok: true });
});`;

  if (loading) {
    return <div className="merchant-page"><div className="merchant-loading">Loading…</div></div>;
  }

  return (
    <div className="merchant-page">
      {/* Stats bar */}
      {stats && (
        <div className="merchant-stats">
          <div className="merchant-stat-card">
            <div className="merchant-stat-label">Balance</div>
            <div className="merchant-stat-value">${parseFloat(stats.balanceUsdt).toFixed(2)} USDT</div>
          </div>
          <div className="merchant-stat-card">
            <div className="merchant-stat-label">Merchant earned</div>
            <div className="merchant-stat-value">${parseFloat(stats.merchantEarnedUsdt).toFixed(2)} USDT</div>
          </div>
          <div className="merchant-stat-card">
            <div className="merchant-stat-label">Merchant orders</div>
            <div className="merchant-stat-value">{stats.merchantOrders}</div>
          </div>
          <div className="merchant-stat-card">
            <div className="merchant-stat-label">Active tokens</div>
            <div className="merchant-stat-value">{stats.activeTokens}</div>
          </div>
        </div>
      )}

      <h1 className="merchant-title">Merchant API</h1>
      <p className="merchant-subtitle">
        Accept payments programmatically. <Link to="/merchant/test" className="merchant-link">Open sandbox →</Link>
      </p>

      {/* Tabs */}
      <div className="merchant-tabs">
        {(['keys', 'orders', 'quickstart', 'webhooks'] as Tab[]).map(t => (
          <button
            key={t}
            className={`merchant-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'keys' ? 'API Keys' : t === 'orders' ? 'Orders' : t === 'quickstart' ? 'Quick Start' : 'Webhooks'}
          </button>
        ))}
      </div>

      {/* API Keys tab */}
      {tab === 'keys' && (
        <div className="merchant-section">
          <div className="merchant-section-header">
            <h2>API Keys</h2>
            <button className="merchant-btn-primary" onClick={() => setShowCreateModal(true)}>
              + Create key pair
            </button>
          </div>
          <div className="merchant-info-box">
            <strong>Publishable key</strong> (safe for browser/JS) — creates checkout sessions.<br />
            <strong>Secret key</strong> (server only) — verifies webhook signatures. Never expose in frontend code.
          </div>
          {tokens.length === 0 ? (
            <div className="merchant-empty">No API keys yet. Create your first key pair.</div>
          ) : (
            <table className="merchant-table">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>Prefix</th><th>Created</th><th>Last used</th><th>Orders</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(t => (
                  <tr key={t.id} className={t.isActive ? '' : 'merchant-row-revoked'}>
                    <td>{t.name}</td>
                    <td><span className={`merchant-badge merchant-badge-${t.type}`}>{t.type}</span></td>
                    <td><code className="merchant-code">{t.prefix}…</code></td>
                    <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                    <td>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : '—'}</td>
                    <td>{t.totalOrders}</td>
                    <td>{t.isActive ? <span className="merchant-status-active">Active</span> : <span className="merchant-status-revoked">Revoked</span>}</td>
                    <td>
                      {t.isActive && (
                        <div className="merchant-row-actions">
                          <button
                            className="merchant-btn-view-sm"
                            onClick={() => handleViewToken(t)}
                            disabled={revealLoading === t.id}
                          >
                            {revealLoading === t.id ? '…' : 'View'}
                          </button>
                          <button className="merchant-btn-danger-sm" onClick={() => handleRevoke(t.id)}>Revoke</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Orders tab */}
      {tab === 'orders' && (
        <div className="merchant-section">
          <div className="merchant-section-header">
            <h2>Orders <span className="merchant-count">({ordersTotal})</span></h2>
          </div>
          <div className="merchant-filters">
            <div className="merchant-filter-sandbox">
              <button
                className={`merchant-filter-btn ${orderFilter.sandbox === '' ? 'active' : ''}`}
                onClick={() => setOrderFilter(f => ({ ...f, sandbox: '' }))}
              >All</button>
              <button
                className={`merchant-filter-btn merchant-filter-btn-live ${orderFilter.sandbox === 'false' ? 'active' : ''}`}
                onClick={() => setOrderFilter(f => ({ ...f, sandbox: 'false' }))}
              >Live</button>
              <button
                className={`merchant-filter-btn merchant-filter-btn-test ${orderFilter.sandbox === 'true' ? 'active' : ''}`}
                onClick={() => setOrderFilter(f => ({ ...f, sandbox: 'true' }))}
              >Test</button>
            </div>
            <select
              value={orderFilter.status}
              onChange={e => setOrderFilter(f => ({ ...f, status: e.target.value }))}
            >
              <option value="">All statuses</option>
              <option value="waiting">Waiting</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="expired">Expired</option>
            </select>
            <input
              type="date"
              value={orderFilter.dateFrom}
              onChange={e => setOrderFilter(f => ({ ...f, dateFrom: e.target.value }))}
              placeholder="Date from"
            />
            <input
              type="date"
              value={orderFilter.dateTo}
              onChange={e => setOrderFilter(f => ({ ...f, dateTo: e.target.value }))}
              placeholder="Date to"
            />
          </div>
          {orders.length === 0 ? (
            <div className="merchant-empty">No merchant orders yet.</div>
          ) : (
            <table className="merchant-table">
              <thead>
                <tr>
                  <th>Order ID</th><th>Merchant ID</th><th>Amount</th><th>Mode</th><th>Status</th><th>Webhook</th><th>Created</th><th>Token</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.orderId}>
                    <td><code className="merchant-code">{o.orderId}</code></td>
                    <td>{o.merchantOrderId ?? '—'}</td>
                    <td>{o.usdtAmount.toFixed(2)} USDT</td>
                    <td>
                      {o.sandbox
                        ? <span className="merchant-badge merchant-badge-sandbox">test</span>
                        : <span className="merchant-badge merchant-badge-live">live</span>}
                    </td>
                    <td><span className={`merchant-status-badge merchant-status-badge-${o.status}`}>{o.status}</span></td>
                    <td>
                      <button
                        className={`merchant-webhook-badge merchant-webhook-${o.webhookStatus} merchant-webhook-btn`}
                        onClick={() => { setWebhookInfoOrder(o); setResendResult(null); }}
                        title="View webhook details"
                      >
                        {o.webhookStatus}
                        {o.webhookAttempts > 0 && <span className="merchant-attempts"> ({o.webhookAttempts})</span>}
                      </button>
                    </td>
                    <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td>{o.token ? <code className="merchant-code">{o.token.prefix}…</code> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Quick Start tab */}
      {tab === 'quickstart' && (
        <div className="merchant-section">
          <h2>Quick Start</h2>
          <p className="merchant-text">Add crypto payments to any website in minutes.</p>

          {!pubToken && (
            <div className="merchant-warning-box" style={{ marginBottom: '24px' }}>
              You don't have an active API key yet.{' '}
              <button className="merchant-link-btn" onClick={() => setTab('keys')}>Create a key pair →</button>
            </div>
          )}

          {/* Step 1 */}
          <div className="qs-step">
            <div className="qs-step-num">1</div>
            <div className="qs-step-body">
              <h3>Load the SDK</h3>
              <p className="merchant-text">Add this script tag to your HTML page, before <code>&lt;/body&gt;</code>:</p>
              <div className="merchant-code-block">
                <button className="merchant-code-copy" onClick={() => handleCopy(`<script src="${sdkUrl}"></script>`, 'sdk-tag')}>
                  {copied === 'sdk-tag' ? '✓ Copied' : 'Copy'}
                </button>
                <pre><code>{`<script src="${sdkUrl}"></script>`}</code></pre>
              </div>
            </div>
          </div>

          {/* Step 2 — two approaches */}
          <div className="qs-step">
            <div className="qs-step-num">2</div>
            <div className="qs-step-body">
              <h3>Initialize payment</h3>
              <p className="merchant-text">Choose your preferred approach:</p>
              <div className="merchant-code-tabs">
                <button className={`merchant-code-tab ${codeTab === 'script' ? 'active' : ''}`} onClick={() => setCodeTab('script')}>
                  Button mode (simplest)
                </button>
                <button className={`merchant-code-tab ${codeTab === 'prog' ? 'active' : ''}`} onClick={() => setCodeTab('prog')}>
                  Programmatic JS
                </button>
              </div>
              <div className="merchant-code-block">
                <button className="merchant-code-copy" onClick={() => handleCopy(codeTab === 'script' ? scriptSnippet : progSnippet, 'code')}>
                  {copied === 'code' ? '✓ Copied' : 'Copy'}
                </button>
                <pre><code>{codeTab === 'script' ? scriptSnippet : progSnippet}</code></pre>
              </div>
              {codeTab === 'script' && (
                <p className="merchant-text" style={{ marginTop: '8px' }}>
                  The SDK auto-scans <code>data-plzpay</code> buttons on page load and attaches click handlers automatically.
                </p>
              )}
            </div>
          </div>

          {/* Step 3 — options reference */}
          <div className="qs-step">
            <div className="qs-step-num">3</div>
            <div className="qs-step-body">
              <h3>SDK options reference</h3>
              <table className="merchant-table qs-params-table">
                <thead>
                  <tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>token</code></td><td>string</td><td>✓</td><td>Your publishable key (<code>plzpay_pub_…</code>)</td></tr>
                  <tr><td><code>amount</code></td><td>number</td><td>✓</td><td>Payment amount</td></tr>
                  <tr><td><code>currency</code></td><td>string</td><td></td><td>USDT · TRX · BTC · ETH (default: USDT)</td></tr>
                  <tr><td><code>orderId</code></td><td>string</td><td></td><td>Your unique order ID for idempotency</td></tr>
                  <tr><td><code>description</code></td><td>string</td><td></td><td>Shown on checkout page</td></tr>
                  <tr><td><code>callbackUrl</code></td><td>string</td><td></td><td>Server webhook URL (POST on payment)</td></tr>
                  <tr><td><code>comebackUrl</code></td><td>string</td><td></td><td>Redirect URL after successful payment</td></tr>
                  <tr><td><code>iframe</code></td><td>boolean</td><td></td><td><code>true</code> = modal overlay · <code>false</code> = full redirect</td></tr>
                  <tr><td><code>ttl</code></td><td>number</td><td></td><td>Checkout TTL in minutes (5–1440, default 60)</td></tr>
                  <tr><td><code>sandbox</code></td><td>boolean</td><td></td><td>Test mode — no real payment. Default: <code>false</code></td></tr>
                  <tr><td><code>onSuccess</code></td><td>function</td><td></td><td>Called when payment confirmed: <code>(data) =&gt; {}</code></td></tr>
                  <tr><td><code>onError</code></td><td>function</td><td></td><td>Called on error: <code>(err) =&gt; {}</code></td></tr>
                  <tr><td><code>onClose</code></td><td>function</td><td></td><td>Called when iframe modal is closed</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Step 4 — test */}
          <div className="qs-step">
            <div className="qs-step-num">4</div>
            <div className="qs-step-body">
              <h3>Test your integration</h3>
              <p className="merchant-text">
                Use the interactive SDK sandbox to test parameters and see live events.
              </p>
              <Link to="/merchant/test" className="merchant-btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                Open SDK Sandbox →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Webhooks tab */}
      {tab === 'webhooks' && (
        <div className="merchant-section">
          <h2>Webhooks</h2>
          <p className="merchant-text">
            When a payment is confirmed, Plz Pay Me sends a <code>POST</code> request to your <code>callbackUrl</code>.
          </p>
          <h3>Payload format</h3>
          <div className="merchant-code-block">
            <pre><code>{JSON.stringify({
  event: 'payment.completed',
  orderId: 'ORD-ABCD1234',
  merchantOrderId: 'shop-order-123',
  amount: 10.00,
  currency: 'USDT',
  status: 'completed',
  txId: 'abc123...',
  timestamp: '2026-02-22T12:00:00.000Z'
}, null, 2)}</code></pre>
          </div>

          <h3>Signature verification</h3>
          <p className="merchant-text">
            Each request includes <code>X-PlzPay-Signature: sha256=&lt;hmac&gt;</code>.
            Verify it using your <strong>secret key</strong>.
          </p>
          <div className="merchant-code-block">
            <button className="merchant-code-copy" onClick={() => handleCopy(webhookVerifySnippet, 'webhook')}>
              {copied === 'webhook' ? '✓ Copied' : 'Copy'}
            </button>
            <pre><code>{webhookVerifySnippet}</code></pre>
          </div>

          <h3>Retry policy</h3>
          <p className="merchant-text">
            If delivery fails, Plz Pay Me retries: <strong>immediately → +30 seconds → +5 minutes</strong>.
            Return HTTP 2xx to acknowledge delivery.
          </p>
        </div>
      )}

      {/* View token modal */}
      {viewToken && (
        <div className="merchant-modal-overlay" onClick={() => setViewToken(null)}>
          <div className="merchant-modal" onClick={e => e.stopPropagation()}>
            <h2>API Key</h2>
            <div className="merchant-token-reveal">
              <div className="merchant-token-reveal-header">
                <span className={`merchant-badge merchant-badge-${viewToken.type}`}>{viewToken.type}</span>
                <span className="merchant-token-name">{viewToken.name}</span>
              </div>
              {viewToken.value ? (
                <div className="merchant-token-reveal-value">
                  <code>{viewToken.value}</code>
                  <button
                    className="merchant-copy-btn"
                    onClick={() => handleCopy(viewToken.value!, `view-${viewToken.id}`)}
                  >
                    {copied === `view-${viewToken.id}` ? '✓' : '📋'}
                  </button>
                </div>
              ) : (
                <div className="merchant-empty">Could not decrypt token.</div>
              )}
              {viewToken.type === 'secret' && (
                <p className="merchant-text" style={{ marginTop: '8px', fontSize: '12px', color: '#f59e0b' }}>
                  ⚠️ Keep this key secret. Never expose it in frontend code or public repositories.
                </p>
              )}
            </div>
            <div className="merchant-modal-actions">
              <button className="merchant-btn-primary" onClick={() => setViewToken(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook info modal */}
      {webhookInfoOrder && (
        <div className="merchant-modal-overlay" onClick={() => setWebhookInfoOrder(null)}>
          <div className="merchant-modal merchant-webhook-modal" onClick={e => e.stopPropagation()}>
            <div className="merchant-webhook-modal-header">
              <h2>Webhook Info</h2>
              <span className="merchant-webhook-order-id">{webhookInfoOrder.orderId}</span>
            </div>

            <div className="merchant-webhook-info-rows">
              <div className="merchant-webhook-info-row">
                <span className="merchant-webhook-info-label">Status</span>
                <span className={`merchant-webhook-badge merchant-webhook-${webhookInfoOrder.webhookStatus}`}>
                  {webhookInfoOrder.webhookStatus}
                </span>
              </div>

              <div className="merchant-webhook-info-row">
                <span className="merchant-webhook-info-label">Attempts</span>
                <span className="merchant-webhook-info-value">
                  {webhookInfoOrder.webhookAttempts === 0
                    ? '—'
                    : `${webhookInfoOrder.webhookAttempts} of 3`}
                </span>
              </div>

              <div className="merchant-webhook-info-row">
                <span className="merchant-webhook-info-label">Callback URL</span>
                <span className="merchant-webhook-info-value merchant-webhook-url">
                  {webhookInfoOrder.callbackUrl ?? <em className="merchant-webhook-none">not set</em>}
                </span>
              </div>

              {webhookInfoOrder.webhookDeliveredAt && (
                <div className="merchant-webhook-info-row">
                  <span className="merchant-webhook-info-label">Delivered at</span>
                  <span className="merchant-webhook-info-value">
                    {new Date(webhookInfoOrder.webhookDeliveredAt).toLocaleString()}
                  </span>
                </div>
              )}

              {webhookInfoOrder.webhookLastError && (
                <div className="merchant-webhook-info-row merchant-webhook-info-row-error">
                  <span className="merchant-webhook-info-label">Last error</span>
                  <span className="merchant-webhook-info-error">{webhookInfoOrder.webhookLastError}</span>
                </div>
              )}

              <div className="merchant-webhook-info-divider" />

              <div className="merchant-webhook-info-row">
                <span className="merchant-webhook-info-label">Payment status</span>
                <span className={`merchant-status-badge merchant-status-badge-${webhookInfoOrder.status}`}>
                  {webhookInfoOrder.status}
                </span>
              </div>

              {webhookInfoOrder.confirmedAt && (
                <div className="merchant-webhook-info-row">
                  <span className="merchant-webhook-info-label">Confirmed at</span>
                  <span className="merchant-webhook-info-value">
                    {new Date(webhookInfoOrder.confirmedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div className="merchant-modal-actions merchant-webhook-modal-actions">
              {webhookInfoOrder.status === 'completed' && webhookInfoOrder.callbackUrl && (
                <div className="merchant-webhook-resend-wrap">
                  <button
                    className="merchant-btn-resend"
                    onClick={() => handleResendWebhook(webhookInfoOrder.orderId)}
                    disabled={resending}
                  >
                    {resending ? 'Sending…' : '↩ Resend webhook'}
                  </button>
                  {resendResult === 'ok' && <span className="merchant-resend-ok">Queued ✓</span>}
                  {resendResult === 'err' && <span className="merchant-resend-err">Failed ✗</span>}
                </div>
              )}
              <button className="merchant-btn-primary" onClick={() => setWebhookInfoOrder(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create token modal */}
      {showCreateModal && (
        <div className="merchant-modal-overlay" onClick={closeCreateModal}>
          <div className="merchant-modal" onClick={e => e.stopPropagation()}>
            {!createdPair ? (
              <>
                <h2>Create API key pair</h2>
                <p className="merchant-text">A publishable key and a secret key will be generated.</p>
                <div className="merchant-form-group">
                  <label>Name (e.g. "My Shop", "Production")</label>
                  <input
                    type="text"
                    value={newTokenName}
                    onChange={e => setNewTokenName(e.target.value)}
                    placeholder="My Shop"
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && !creating && handleCreatePair()}
                  />
                </div>
                <div className="merchant-modal-actions">
                  <button className="merchant-btn-secondary" onClick={closeCreateModal}>Cancel</button>
                  <button className="merchant-btn-primary" onClick={handleCreatePair} disabled={creating}>
                    {creating ? 'Creating…' : 'Create'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>Keys created!</h2>
                <div className="merchant-warning-box">
                  ⚠️ Save these tokens now — they will <strong>not</strong> be shown again.<br />
                  Use the <strong>publishable key</strong> in browser/SDK. Keep the <strong>secret key</strong> on your server only.
                </div>
                {[createdPair.publishable, createdPair.secret].map(k => (
                  <div key={k.type} className="merchant-token-reveal">
                    <div className="merchant-token-reveal-header">
                      <span className={`merchant-badge merchant-badge-${k.type}`}>{k.type}</span>
                      <span className="merchant-token-name">{k.name}</span>
                    </div>
                    <div className="merchant-token-reveal-value">
                      <code>{k.token}</code>
                      <button
                        className="merchant-copy-btn"
                        onClick={() => handleCopy(k.token, k.type)}
                      >
                        {copied === k.type ? '✓' : '📋'}
                      </button>
                    </div>
                  </div>
                ))}
                <div className="merchant-modal-actions">
                  <button className="merchant-btn-primary" onClick={closeCreateModal}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
